<script lang="ts">
    import {Button, Card, CardActions, CardText, CardTitle, CardSubtitle, Dialog, TextField} from 'svelte-materialify';
    import {Table} from 'svelte-materialify/src';
    import {meetings} from "../stores";
    import {getItemById} from "../util";
    import MemberComponent from "./Member.svelte";
    import {kSeats} from "../rules";
    import {cloneDeep, isEqual} from "lodash";
    import type {Attendee, Meeting, Member} from "../firestore";
    import {slide} from "svelte/transition";
    import {updateMeeting, getAllClubMembers, isMeetingFull} from "../firestore";

    let meetingId: string;
    let savedMeeting: Meeting | null, meeting: Meeting | null;
    let isOpen: boolean;
    let newMemberEmail: string = undefined;

    export function open(id: string) {
        meetingId = id;
        savedMeeting = getItemById($meetings, id);
        meeting = cloneDeep(savedMeeting);
        savedMeeting = Object.freeze(savedMeeting);
        isOpen = true;
    }

    function seatOptions(meeting: Meeting, currentSeat?: string): string[] {
        return kSeats.filter(it => !meeting.attendees.some(a => a.seat === it))
            .concat(currentSeat === undefined ? [] : [currentSeat]).sort();
    }

    const removeMember = (attendee: Attendee) =>
        meeting.attendees = meeting.attendees.filter(it => it.identity.email !== attendee.identity.email);

    const update = async () => {
        await updateMeeting(meeting);
        savedMeeting = meeting;
        isOpen = false;
    };

    const addMember = (members: Member[]) => {
        meeting.attendees = [...meeting.attendees, {
            identity: members.find(it => it.email === newMemberEmail)!,
            seat: seatOptions(meeting)[0],
            goal: '',
            present: null
        }];
        newMemberEmail = undefined;
    };

    const close = () => {
        meeting = savedMeeting;
        isOpen = false;
    }

    $: meetingFull = meeting == null ? false : meeting.attendees.length >= meeting.capacity;
</script>

<div class="meeting-editor">

    <Dialog bind:active={isOpen} fullscreen>
        <Card>
            <div class="close-container float-right ma-2">
                <Button icon on:click={close} size="large">
                    <span class="material-icons">close</span>
                </Button>
            </div>
            <CardTitle>Edit {meetingId}</CardTitle>
            <CardText>
                {#if meeting == null}
                    <i>Loading...</i>
                {:else}
                <Table dense style="overflow:hidden">
                    <thead>
                    <tr>
                        <th>Attendee</th>
                        <th>Seat</th>
                        <th>Goal</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody class="meeting-edit-table">
                    {#each meeting.attendees as attendee (attendee.identity.email)}
                        <tr>
                            <td>
                                <MemberComponent value={attendee.identity} />
                            </td>
                            <td>
                                <select bind:value={attendee.seat}>
                                    {#each seatOptions(meeting, attendee.seat) as opt}
                                        <option value={opt}>{opt}</option>
                                    {/each}
                                </select>
                            </td>
                            <td>
                                <TextField bind:value={attendee.goal} />
                            </td>
                            <td>
                                <Button icon on:click={() => removeMember(attendee)}>
                                    <span class="red-text material-icons">delete</span>
                                </Button>
                            </td>
                        </tr>
                    {/each}
                    </tbody>
                </Table>
                    {#if meeting != null && !meetingFull}
                        {#await getAllClubMembers()}
                            <i>Loading members...</i>
                        {:then members}
                    <div class="d-flex pa-3">
                            <select bind:value={newMemberEmail}>
                                <option value={undefined}>Select member to add...</option>
                                {#each members as member}
                                    {#if meeting.attendees.every(a => a.identity.email !== member.email)}
                                        <option value={member.email}>{member.name}</option>
                                    {/if}
                                {/each}
                            </select>
                        {#if newMemberEmail !== undefined}
                        <Button class="secondary-text" text on:click={() => addMember(members)}>
                            <span class="material-icons">add</span>&nbsp;
                            Add Member
                        </Button>
                        {/if}
                    </div>
                        {/await}
                    {/if}
                {/if}
            </CardText>
            {#if !isEqual(meeting, savedMeeting)}
                <div transition:slide>
                    <CardActions>
                        <Button class="primary-text" text on:click={update}>Update</Button>
                    </CardActions>
                </div>
            {/if}
        </Card>
    </Dialog>
</div>

<style>
    .meeting-editor select {
        appearance: auto;
        border-style: solid;
    }
</style>